import {
  createContext,
  memo,
  useContext,
  useId,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import ToolTip from "../../../../../parts/tooltip";
import InsightsDeltaBadge, {
  insightsDeltaPillVisible,
} from "./InsightsDeltaBadge";

/** Default: keep the delta lane (safe if a uniform row renders outside a provider). */
const InsightsMetricUniformContext = createContext({ reserveDeltaSlot: true });

/**
 * Wrap a block of `InsightsMetricRow` with `uniformLayout`. Set `reserveDeltaSlot` once
 * for the whole block (`insightsUniformRowsReserveDeltaLane(metrics)`), not on every row.
 */
export function InsightsMetricUniformProvider({ reserveDeltaSlot, children }) {
  const value = useMemo(
    () => ({ reserveDeltaSlot }),
    [reserveDeltaSlot]
  );

  return (
    <InsightsMetricUniformContext.Provider value={value}>
      {children}
    </InsightsMetricUniformContext.Provider>
  );
}

InsightsMetricUniformProvider.propTypes = {
  reserveDeltaSlot: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Single label + value row with optional info affordance and optional delta badge.
 * @param {boolean} [uniformLayout] — when true (inside `InsightsMetricUniformProvider`), fixed slots for info + optional delta lane per block settings.
 */
function InsightsMetricRow({
  label,
  valueDisplay,
  valueSuffix,
  delta,
  infoAriaLabel,
  infoTooltipBody,
  infoTooltipHintClassNames = "",
  uniformLayout = false,
  showNeutralDeltaBadge = false,
  /** Lift cards: ellipsize the label (e.g. “Volume”) so the numeric value stays full width. */
  truncateLabel = false,
}) {
  const { reserveDeltaSlot } = useContext(InsightsMetricUniformContext);
  const showDeltaLane = !uniformLayout || reserveDeltaSlot;

  const metricInfoTooltipId = `program-insights-metric-info-${useId().replaceAll(":", "")}`;

  const infoControl = infoAriaLabel ? (
    <ToolTip
      id={metricInfoTooltipId}
      className="ProgramInsights-metricRow-info"
      body={infoTooltipBody ?? infoAriaLabel}
      hintClassNames={infoTooltipHintClassNames}
    >
      <span
        className="ProgramInsights-metricRow-infoHint"
        role="img"
        aria-label={infoAriaLabel}
      >
        ?
      </span>
    </ToolTip>
  ) : null;

  const showDeltaPill =
    insightsDeltaPillVisible(delta) ||
    (showNeutralDeltaBadge && delta?.tone === "neutral");

  const deltaBadge = showDeltaPill ? (
    <InsightsDeltaBadge delta={delta.value} tone={delta.tone} />
  ) : null;

  const deltaCellContent = showDeltaPill ? (
    deltaBadge
  ) : (
    <span
      className="ProgramInsights-muscleRow-deltaSpacer"
      aria-hidden="true"
    />
  );

  /**
   * classes for the value cell
   * - ProgramInsights-metricRow-value: base class
   * - ProgramInsights-metricRow-value--uniform: when uniform layout is enabled
   * - ProgramInsights-metricRow-value--uniformNoDelta: when uniform layout is enabled and the delta lane is not shown
   */
  const valueCellClassNames = ["ProgramInsights-metricRow-value"];
  if (uniformLayout) {
    valueCellClassNames.push("ProgramInsights-metricRow-value--uniform");
    if (!showDeltaLane) {
      valueCellClassNames.push("ProgramInsights-metricRow-value--uniformNoDelta");
    }
  }

  return (
    <div
      className={`ProgramInsights-metricRow${uniformLayout ? " ProgramInsights-metricRow--uniform" : ""}${valueSuffix ? " ProgramInsights-metricRow--volume" : ""}${truncateLabel ? " ProgramInsights-metricRow--truncateLabel" : ""}`}
    >
      <div
        className={`ProgramInsights-metricRow-label${uniformLayout ? " ProgramInsights-metricRow-label--uniform" : ""}`}
      >
        <span className="ProgramInsights-metricRow-labelText">{label}</span>
        {uniformLayout ? (
          <span
            className="ProgramInsights-metricRow-infoSlot"
            aria-hidden={!infoAriaLabel}
          >
            {infoControl}
          </span>
        ) : (
          infoControl
        )}
      </div>
      <div className={valueCellClassNames.join(" ")}>
        <span className="ProgramInsights-metricRow-valuePrimary">
          <span
            className={`ProgramInsights-metricRow-number${
              valueSuffix ? " ProgramInsights-metricRow-number--volumeLead" : ""
            }`}
          >
            {valueDisplay}
          </span>
          {valueSuffix ? (
            <span className="ProgramInsights-metricRow-valueSuffix">
              {valueSuffix}
            </span>
          ) : null}
        </span>
        {uniformLayout && showDeltaLane ? (
          <span
            className="ProgramInsights-metricRow-deltaCell"
            aria-hidden={!showDeltaPill}
          >
            {deltaCellContent}
          </span>
        ) : null}
        {uniformLayout ? null : deltaBadge}
      </div>
    </div>
  );
}

InsightsMetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  valueDisplay: PropTypes.string.isRequired,
  /** e.g. ` kg` for volume — styled lighter/smaller than the numeric span. */
  valueSuffix: PropTypes.string,
  delta: PropTypes.shape({
    value: PropTypes.number.isRequired,
    tone: PropTypes.oneOf(["up", "down", "neutral"]).isRequired,
  }),
  infoAriaLabel: PropTypes.string,
  /** Tooltip body when it should differ from `infoAriaLabel` (defaults to aria label). */
  infoTooltipBody: PropTypes.string,
  infoTooltipHintClassNames: PropTypes.string,
  uniformLayout: PropTypes.bool,
  /** When true, render the neutral “—” pill for `delta.tone === "neutral"` (same width as up/down). */
  showNeutralDeltaBadge: PropTypes.bool,
  truncateLabel: PropTypes.bool,
};

export default memo(InsightsMetricRow);
